import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard,
  DollarSign,
  Send,
  QrCode,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  type PaymentMethodType,
  useGetPaymentInstructions,
  useCreatePaymentInstruction,
  useUpdatePaymentInstruction,
  useDeletePaymentInstruction,
} from "@/api/payments";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PaymentInstruction {
  id: string;
  method: PaymentMethodType;
  displayName: string;
  instructions?: string;
  isActive: boolean;
  eTransferEmail?: string;
  wechatQRCode?: string;
}

interface PaymentInstructionForm {
  method: PaymentMethodType;
  displayName: string;
  instructions: string;
  isActive: boolean;
  eTransferEmail?: string;
  wechatQRCode?: string;
}

export function PaymentInstructionsManager() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instructionToDelete, setInstructionToDelete] = useState<string | null>(
    null
  );

  const [formData, setFormData] = useState<PaymentInstructionForm>({
    method: "CASH",
    displayName: "",
    instructions: "",
    isActive: true,
  });

  const {
    data: instructions,
    isLoading,
    refetch,
  } = useGetPaymentInstructions();

  const createMutation = useCreatePaymentInstruction({
    onSuccess: () => {
      toast.success("Payment Instruction Created", {
        description: "The payment instruction has been created successfully",
      });
      setCreateDialogOpen(false);
      resetForm();
      void refetch();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Create", {
        description:
          err.response?.data?.error || "Could not create payment instruction",
      });
    },
  });

  const updateMutation = useUpdatePaymentInstruction({
    onSuccess: () => {
      toast.success("Payment Instruction Updated", {
        description: "The payment instruction has been updated successfully",
      });
      setEditingId(null);
      resetForm();
      void refetch();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Update", {
        description:
          err.response?.data?.error || "Could not update payment instruction",
      });
    },
  });

  const deleteMutation = useDeletePaymentInstruction({
    onSuccess: () => {
      toast.success("Payment Instruction Deleted", {
        description: "The payment instruction has been deleted successfully",
      });
      setDeleteDialogOpen(false);
      setInstructionToDelete(null);
      void refetch();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Delete", {
        description:
          err.response?.data?.error || "Could not delete payment instruction",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      method: "CASH",
      displayName: "",
      instructions: "",
      isActive: true,
    });
  };

  const handleEdit = (instruction: PaymentInstruction) => {
    setEditingId(instruction.id);
    setFormData({
      method: instruction.method,
      displayName: instruction.displayName,
      instructions: instruction.instructions || "",
      isActive: instruction.isActive,
      eTransferEmail: instruction.eTransferEmail,
      wechatQRCode: instruction.wechatQRCode,
    });
  };

  const handleSave = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = () => {
    if (instructionToDelete) {
      deleteMutation.mutate(instructionToDelete);
    }
  };

  const getMethodIcon = (method: PaymentMethodType) => {
    switch (method) {
      case "SAVED_CARD":
        return <CreditCard className="h-4 w-4" />;
      case "E_TRANSFER":
        return <Send className="h-4 w-4" />;
      case "WECHAT":
        return <QrCode className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const paymentMethods: PaymentMethodType[] = [
    "SAVED_CARD",
    "CASH",
    "E_TRANSFER",
    "WECHAT",
    "INVOICE",
    "CREDIT",
  ];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Instructions Management</CardTitle>
              <CardDescription>
                Configure payment instructions for different payment methods
              </CardDescription>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Instruction
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {instructions && instructions.length > 0 ? (
            <div className="space-y-4">
              {instructions.map((instruction: PaymentInstruction) => (
                <Card key={instruction.id}>
                  <CardContent className="pt-6">
                    {editingId === instruction.id ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Display Name</Label>
                            <Input
                              value={formData.displayName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  displayName: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label>Status</Label>
                              <div className="flex items-center space-x-2">
                                <Switch
                                  checked={formData.isActive}
                                  onCheckedChange={(checked) =>
                                    setFormData({
                                      ...formData,
                                      isActive: checked,
                                    })
                                  }
                                />
                                <span className="text-sm">
                                  {formData.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label>Instructions</Label>
                          <Textarea
                            value={formData.instructions}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                instructions: e.target.value,
                              })
                            }
                            rows={4}
                            placeholder="Enter payment instructions that will be shown to customers"
                          />
                        </div>

                        {formData.method === "E_TRANSFER" && (
                          <div>
                            <Label>E-Transfer Email</Label>
                            <Input
                              type="email"
                              value={formData.eTransferEmail || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  eTransferEmail: e.target.value,
                                })
                              }
                              placeholder="payment@company.com"
                            />
                          </div>
                        )}

                        {formData.method === "WECHAT" && (
                          <div>
                            <Label>WeChat QR Code URL</Label>
                            <Input
                              value={formData.wechatQRCode || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  wechatQRCode: e.target.value,
                                })
                              }
                              placeholder="https://example.com/wechat-qr.png"
                            />
                          </div>
                        )}

                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingId(null);
                              resetForm();
                            }}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={updateMutation.isPending}
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="mr-2 h-4 w-4" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {getMethodIcon(instruction.method)}
                            <span className="font-medium">
                              {instruction.displayName}
                            </span>
                            <Badge
                              variant={
                                instruction.isActive ? "default" : "secondary"
                              }
                            >
                              {instruction.isActive ? "Active" : "Inactive"}
                            </Badge>
                            <Badge variant="outline">
                              {instruction.method}
                            </Badge>
                          </div>
                          {instruction.instructions && (
                            <p className="text-sm text-muted-foreground">
                              {instruction.instructions}
                            </p>
                          )}
                          {instruction.eTransferEmail && (
                            <p className="text-sm">
                              <span className="text-muted-foreground">
                                E-Transfer Email:
                              </span>{" "}
                              <code className="bg-muted px-1 rounded">
                                {instruction.eTransferEmail}
                              </code>
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(instruction)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setInstructionToDelete(instruction.id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No payment instructions configured. Click "Add Instruction" to
                create one.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Payment Instruction</DialogTitle>
            <DialogDescription>
              Add payment instructions for a specific payment method
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={formData.method}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      method: e.target.value as PaymentMethodType,
                    })
                  }
                >
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {method.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  placeholder="e.g., Cash Payment"
                />
              </div>
            </div>

            <div>
              <Label>Instructions</Label>
              <Textarea
                value={formData.instructions}
                onChange={(e) =>
                  setFormData({ ...formData, instructions: e.target.value })
                }
                rows={4}
                placeholder="Enter payment instructions that will be shown to customers"
              />
            </div>

            {formData.method === "E_TRANSFER" && (
              <div>
                <Label>E-Transfer Email</Label>
                <Input
                  type="email"
                  value={formData.eTransferEmail || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, eTransferEmail: e.target.value })
                  }
                  placeholder="payment@company.com"
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isActive: checked })
                }
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment Instruction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment instruction? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
